import * as React from "react";
import { Cell2Input } from "src/components/Sheet2/components";
import { PropsCell } from "src/components/Sheet2/PropsCell2";
import { imgSrc } from "src/components/PlmUploader/imgSrc";
import { ifDef } from "src/common/ifDef";
import { Cell2Text } from "src/components/Sheet2/components/Cell2Text";
import { TextAreaProps } from "antd/es/input";
import { Cell2 } from "src/components/Sheet2/Cell2";
import { Bom2aPositionsStore } from "../Bom2aPositionsStore";
import { Bom2aColumn } from "./Bom2aColumn";
import {
  Bom2aPosSelection,
  Bom2aSelectionHdr,
} from "./components/Bom2aSelection";
import {
  Bom2aEdFields,
  Bom2aEdPositionRow,
  bom2aUiSize,
} from "../Bom2aPositionsTypes";
import { Bom2aCellDescription } from "./components/Bom2aCellDescription";
import { Bom2aPhotoCell } from "./components/Bom2aPhotoCell/Bom2aPhotoCell";
import { Bom2aColorCell } from "./components/Bom2aColorCell";
import { Bom2aMaterialCell } from "./components/Bom2aMaterialCell";

const mkCellKey = (row: Bom2aEdPositionRow, key: string) => `${row.id}:${key}`;

export const bom2aColumns = (store: Bom2aPositionsStore): Bom2aColumn[] => [
  {
    key: "@Selection",
    title: <Bom2aSelectionHdr store={store} />,
    width: "40px",
    render: (row) => <Bom2aPosSelection posId={row.id} store={store} />,
    view: (row) => <Bom2aPosSelection posId={row.id} store={store} />,
    condition: () => false,
    isCell: false,
  },
  {
    key: "material",
    title: "Material",
    width: "1fr",
    minWidth: 160,
    render: (row) => <Bom2aMaterialCell row={row} store={store} />,
    view: (row) => row.materialCategory.name,
    isCell: false,
  },
  {
    key: "description",
    title: "Description",
    width: "1fr",
    minWidth: 180,
    render: (row, rowIndex) => (
      <Bom2aCellDescription
        row={row}
        store={store}
        pos={makePos(store, "description", rowIndex)}
        bomPosition={store.getRowPosition(row.id)}
      />
    ),
    view: (row) => row.fields.descriptionManualValue.value,
    isCell: true,
  },
  {
    key: "photo",
    title: "Photo",
    width: "1fr",
    minWidth: 160,
    render: (row) => <Bom2aPhotoCell row={row} store={store} />,
    view: (row) =>
      ifDef(row.fields.photo.value, (uid) => (
        <img
          src={imgSrc(uid, "small")}
          alt=""
          style={{ maxWidth: "100%", maxHeight: 64, objectFit: "contain" }}
        />
      )) ?? null,
    isCell: false,
  },
  {
    key: "composition",
    title: "Composition",
    width: "1fr",
    minWidth: 160,
    render: (row, rowIndex) => (
      <Cell2Text
        cell={strCell({ store, row, rowIndex, fieldKey: "composition" })}
        editorProps={editorProps}
      />
    ),
    view: (row) => row.fields.composition.value,
    isCell: true,
  },
  {
    key: "location",
    title: "Location",
    minWidth: 180,
    width: "1fr",
    render: (row, rowIndex) => (
      <Cell2Input
        cell={strCell({ store, row, rowIndex, fieldKey: "location" })}
        editorProps={textProps}
      />
    ),
    view: (row) => row.fields.location.value,
    isCell: true,
  },
  {
    key: "size",
    title: "Trim size",
    width: "1fr",
    minWidth: 180,
    render: (row, rowIndex) => (
      <Cell2Input
        cell={strCell({ store, row, rowIndex, fieldKey: "size" })}
        editorProps={editorProps}
      />
    ),
    view: (row) => row.fields.size.value,
    isCell: true,
  },
  ...store.colorColumns.map(
    ({ id, name }, i) =>
      ({
        key: `col:${id}`,
        title: name,
        width: "1fr",
        minWidth: 120,
        render: (row) => (
          <Bom2aColorCell
            row={row}
            colorModel={row.colorModels[i]}
            store={store}
          />
        ),
        view: (row) => (
          <Bom2aColorCell
            row={row}
            colorModel={row.colorModels[i]}
            store={store}
            disabled
          />
        ),
        isCell: false,
      }) satisfies Bom2aColumn,
  ),
  {
    key: "comment",
    title: "Comments",
    width: "1fr",
    minWidth: 180 * 2,
    render: (row, rowIndex) => (
      <Cell2Text
        cell={strCell({ store, row, rowIndex, fieldKey: "comment" })}
        editorProps={editorProps}
      />
    ),
    view: (row) => row.fields.comment.value,
    isCell: true,
  },
];

const editorProps = { allowClear: true, size: bom2aUiSize };

const textProps = {
  ...editorProps,
  style: { height: "100%" },
} satisfies TextAreaProps;

type ParamsCell = {
  store: Bom2aPositionsStore;
  row: Bom2aEdPositionRow;
  fieldKey: keyof Bom2aEdFields;
  rowIndex: number;
};

const strCell = ({
  store,
  row,
  fieldKey,
  rowIndex,
}: ParamsCell): PropsCell => ({
  cellKey: mkCellKey(row, fieldKey),
  store: store.sheetStore,
  pos: makePos(store, fieldKey, rowIndex),
  emptyValue: "",
  value: row.fields[fieldKey].value,
  save: async (value) => {
    await store.saveFieldValue(
      row.id,
      row.fields[fieldKey].id,
      value as string,
    );
    return () => {
      store.updateFieldValue(row.id, fieldKey, String(value));
    };
  },
});

const makePos = (
  store: Bom2aPositionsStore,
  key: string,
  rowIndex: number,
): Cell2["pos"] => {
  const { posMap } = store;
  const x = posMap[key] ?? -1;
  return { x, y: rowIndex };
};
